import { onSnapshot, collection, addDoc, deleteDoc, doc, setDoc, query, where, getDocs, getDoc, writeBatch } from "firebase/firestore";
import { useState, useEffect } from "react";
import _ from "lodash";
import { db } from "../firebaseConfig";
import logger from "../utils/logger";
import { commitLedgerTransactionsBatchFn, toEpochMs } from "./ledgerServices";
import { AllTransactionType, FetchState, RecurringTransactionRule, LedgerTransaction, TransactionStatus, TransactionSource } from "../global/types";

const COLLECTION = "recurringTransactionRules";
const LEDGER_COLLECTION = "ledger_transactions";

/* RULE LIFECYCLE
    1 - If deleted, remove all ledger_transactions generated by it (recurringRuleId match)
    2 - If updated and the frequency/interval/startDate changes, remove all ledger_transactions generated by it
        (the historical "paid" instances no longer correspond to the new schedule)
*/

export function useLiveRecurringTransactionRules(userId: string | null, type?: AllTransactionType) {
    const [state, setState] = useState<FetchState<RecurringTransactionRule[]>>({
        data: null,
        loading: !!userId,
        error: null,
    });

    useEffect(() => {
        if (!userId) {
            setState({ data: null, loading: false, error: null });
            return;
        }

        setState(prevState => ({ ...prevState, loading: true, error: null }));

        const unsubscribe = onSnapshot(
            query(
                collection(db, COLLECTION),
                ...[
                    where("userId", "==", userId),
                    type ? where("template.type", "==", type) : null
                ].filter(c => c !== null)
            ),
            (snapshot) => {
                const rules: RecurringTransactionRule[] = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...(doc.data() as Omit<RecurringTransactionRule, 'id'>)
                }));
                setState({ data: rules, loading: false, error: null });
            },
            (error) => {
                console.error("Failed to fetch recurring transaction rules:", error);
                setState({ data: null, loading: false, error: "Failed to fetch rule data" });
            }
        );

        return () => unsubscribe();
    }, [userId, type]);

    return state;
}

export function useFetchRecurringTransactionRules(userId: string | null) {
    const [data, setData] = useState<RecurringTransactionRule[] | null>(null);
    const [loading, setLoading] = useState<boolean>(!!userId);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!userId) {
            setData(null);
            setLoading(false);
            return;
        }

        const fetchRules = async () => {
            try {
                setLoading(true);
                const rulesQuery = query(collection(db, COLLECTION), where("userId", "==", userId));
                const snapshot = await getDocs(rulesQuery);
                const rules: RecurringTransactionRule[] = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...(doc.data() as Omit<RecurringTransactionRule, 'id'>)
                }));
                setData(rules);
            } catch (err) {
                console.error("Error fetching recurring transaction rules:", err);
                setError("Failed to fetch rule data");
            } finally {
                setLoading(false);
            }
        };

        fetchRules();
    }, [userId]);

    return { data, loading, error };
}

export async function addRecurringTransactionRule(rule: Omit<RecurringTransactionRule, "id">): Promise<RecurringTransactionRule> {
    const docRef = await addDoc(collection(db, COLLECTION), rule);
    return {
        ...rule,
        id: docRef.id
    };
}

export async function updateRecurringTransactionRule(ruleId: string, updates: Partial<RecurringTransactionRule>) {
    const ruleRef = doc(db, COLLECTION, ruleId);
    const ruleSnap = await getDoc(ruleRef);

    if (!ruleSnap.exists()) {
        throw new Error("RecurringTransactionRule not found");
    }

    const prevRule = ruleSnap.data() as RecurringTransactionRule;

    const scheduleChanged =
        (updates.frequency !== undefined && updates.frequency !== prevRule.frequency) ||
        (updates.interval !== undefined && updates.interval !== prevRule.interval) ||
        (updates.startDate !== undefined && !updates.startDate.isEqual(prevRule.startDate));

    if (scheduleChanged) {
        await deleteRuleGeneratedTransactions(ruleId);
    }

    return setDoc(ruleRef, updates, { merge: true });
}

export async function deleteRecurringTransactionRule(ruleId: string) {
    await deleteRuleGeneratedTransactions(ruleId);
    return deleteDoc(doc(db, COLLECTION, ruleId));
}

export async function bulkDeleteRecurringTransactionRules(ruleIds: string[]) {
    const batch = writeBatch(db);
    ruleIds.forEach(id => batch.delete(doc(db, COLLECTION, id)));
    await batch.commit();

    for (const ruleId of ruleIds) {
        await deleteRuleGeneratedTransactions(ruleId);
    }
}

async function deleteRuleGeneratedTransactions(ruleId: string) {
    const snapshot = await getDocs(query(
        collection(db, LEDGER_COLLECTION),
        where("recurringRuleId", "==", ruleId)
    ));
    if (snapshot.empty) return;

    const batch = writeBatch(db);
    snapshot.docs.forEach(doc => batch.delete(doc.ref));
    await batch.commit();
}

export function useRecurringRuleSubCategoryIds(userId: string | null): string[] | null {
    const [ids, setIds] = useState<string[] | null>(null);

    useEffect(() => {
        if (!userId) {
            setIds(null);
            return;
        }

        getDocs(query(collection(db, COLLECTION), where("userId", "==", userId)))
            .then(snapshot => {
                setIds(
                    snapshot.docs
                        .map(doc => doc.data() as RecurringTransactionRule)
                        .map(data => data.template.categoryId)
                        .filter((id): id is string => !!id)
                );
            })
            .catch(() => setIds([]));
    }, [userId]);

    return ids;
}

export async function fetchLedgerTransactionsForRules(userId: string | null, ruleIds: (string | null)[]): Promise<LedgerTransaction[]> {
    if (!userId || !ruleIds || ruleIds.length === 0) {
        return [];
    }

    const transactionsPromises = ruleIds.map(async (ruleId) => {
        if (!ruleId) return [];
        const snapshot = await getDocs(query(
            collection(db, LEDGER_COLLECTION),
            where("recurringRuleId", "==", ruleId)
        ));
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...(doc.data() as Omit<LedgerTransaction, 'id'>)
        }));
    });

    const transactionsArrays = await Promise.all(transactionsPromises);
    return transactionsArrays.flat();
}

/**
 * Marks recurring-rule occurrences as paid/unpaid, writing/deleting the corresponding
 * `ledger_transactions` doc. `isVirtual` must be supplied by the caller (true for an
 * unmaterialized occurrence built via `materializeVirtualOccurrence`, false for a real,
 * previously-fetched doc) rather than inferred from the id shape.
 *
 * Materializing a virtual occurrence assigns it a brand-new Firestore doc id (distinct
 * from its synthetic `${ruleId}_${timestamp}` id).
 */
export async function setOccurrencesPaidStatus(
    occurrences: { transaction: LedgerTransaction; isVirtual: boolean }[],
    isPaid: boolean
): Promise<{ oldId: string; newId: string }[]> {
    if (!isPaid) {
        const toDelete = occurrences.filter(({ isVirtual }) => !isVirtual);
        for (const chunk of _.chunk(toDelete, 500)) {
            const batch = writeBatch(db);
            for (const { transaction } of chunk) {
                batch.delete(doc(db, LEDGER_COLLECTION, transaction.id));
            }
            await batch.commit();
        }
        return [];
    }

    // Posting an occurrence writes a real ledger row, so it goes through the server like any
    // other: written here it would carry no Money Snapshot and drop out of every converted total.
    const rows = occurrences.map(({ transaction, isVirtual }) => ({
        ...(isVirtual ? {} : { id: transaction.id }),
        accountId: transaction.accountId,
        amount: transaction.amount,
        date: toEpochMs(transaction.date),
        type: transaction.type,
        categoryId: transaction.categoryId,
        categoryName: transaction.categoryName,
        categoryGroup: transaction.categoryGroup,
        description: transaction.description,
        recurringRuleId: transaction.recurringRuleId,
        status: TransactionStatus.POSTED,
        source: TransactionSource.RECURRING_RULE
    }));

    const { data } = await commitLedgerTransactionsBatchFn({ rows: removeUndefinedRows(rows) });

    if (data.rejected.length > 0) {
        logger.warn('Some recurring occurrences were refused', data.rejected);
    }

    // A rejected row has no id, so there is nothing for the screen to follow it to.
    return occurrences
        .map(({ transaction, isVirtual }, index) => ({ oldId: transaction.id, newId: data.ids[index], isVirtual }))
        .filter((entry): entry is { oldId: string; newId: string; isVirtual: boolean } => entry.isVirtual && entry.newId !== null)
        .map(({ oldId, newId }) => ({ oldId, newId }));
}

function removeUndefinedRows(rows: Record<string, unknown>[]): Record<string, unknown>[] {
    return rows.map((row) => Object.fromEntries(Object.entries(row).filter(([, value]) => value !== undefined)));
}

export async function setOccurrencePaidStatus(transaction: LedgerTransaction, isVirtual: boolean, isPaid: boolean): Promise<string | undefined> {
    const mappings = await setOccurrencesPaidStatus([{ transaction, isVirtual }], isPaid);
    return mappings[0]?.newId;
}
